import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeadMeta } from 'components/common/HeadMeta';
import { HeaderNav, HeaderNavCurLink } from 'components/common/HeaderNav';
import { Layout } from 'components/resort/Layout/Layout';
import { Main } from 'components/resort/Layout/Main';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import React from 'react';
import {
  fetchBackgroundImages,
  fetchGuideHomeFeatured, fetchRegionsListGuide, fetchGuideHomeTopRatedResorts,
} from 'api/home/static';
import {
  IBackgroundImages,
  IGuideHomeTopRated, ResortTopRated,
} from 'api/home/types';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BlockContent } from 'components/blocks/BlockContent';
import { BestResorts } from 'components/home/BestResorts';
import { ViewRegions } from 'components/home/ViewRegions';
import { IRegionAllPathsInfo, IRegionFeaturedResort } from 'api/region/types';
import { Routes } from 'routes';
import IntegrityError from 'page-components/400';
import RateResortSearch from 'components/common/RateResortSearch/RateResortSearch';
import { wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { HeroNav } from 'components/common/HeroNav';
import { ELocale } from 'i18n/localeEnum';
import { getLocale } from 'i18n/locale';
import WeSkiWidget from 'components/common/PlanYourTrip/WeSki/WeSkiWidget';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';

const SHOULD_SHOW_NEW_EXPERIENCE = ![ELocale.IT_IT].includes(getLocale() as ELocale);

export type GuidePageProps = {
  guideHomeTopRatedFirstPage: IGuideHomeTopRated;
  regions: IRegionAllPathsInfo;
  featured: IRegionFeaturedResort;
  backgroundImage: IBackgroundImages;
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '3.0';
export const TEMPLATE_NAME = 'Guide Home Page';

export default function GuidePage({
  guideHomeTopRatedFirstPage, regions, featured, backgroundImage,
}: GuidePageProps) {
  const { t } = useOTSTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');

  if (!featured || !backgroundImage) {
    return <IntegrityError />;
  }

  const content = SHOULD_SHOW_NEW_EXPERIENCE ? (
    <>
      <WeSkiWidget destinationId="flexible" />
      {guideHomeTopRatedFirstPage && <BestResorts resortTopRatingOverall={guideHomeTopRatedFirstPage} />}

      <RateResortSearch regionTitle={featured.details.region.title} />

      <AiAssistantWidget
        templateId={TEMPLATE_ID}
        margin={{ marginTop: '-0.75rem', marginBottom: '1.75rem' }}
      />
      {regions && <ViewRegions regions={regions} link={Routes.regionTripPlanningOverview} />}
    </>
  ) : (
    <>
      <RateResortSearch regionTitle={featured.details.region.title} />
      {guideHomeTopRatedFirstPage && <BestResorts resortTopRatingOverall={guideHomeTopRatedFirstPage} />}

      <AiAssistantWidget
        templateId={TEMPLATE_ID}
        margin={{ marginTop: '-0.75rem', marginBottom: '1.75rem' }}
      />
      {regions && <ViewRegions regions={regions} link={Routes.regionTripPlanningOverview} />}
    </>
  );

  return (
    <AdUnitContext
      adType="home"
      adTKey="other travel"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeadMeta
        title={seoT('Other.resort_guide.title')}
        description={seoT('Other.resort_guide.description')}
        image={backgroundImage.image}
      />
      <HeaderNav current={HeaderNavCurLink.TRIP_PLANNING} />
      <TopAd id="ski-resorts" />
      <div className="container-xl content-container">
        <HeroNav
          title={t('guide.home.heroTitle')}
          backgroundPhoto={backgroundImage.image}
        />

        <Layout className="layout-container">
          <Main>
            <BoxWithShadow>
              <BlockHeader title={t('guide.home.seoTitle')} />
              <BlockContent>
                {t('guide.home.seoText')}
              </BlockContent>
            </BoxWithShadow>
            {content}
          </Main>
        </Layout>
      </div>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

export const fetchProps = async (): Promise<GuidePageProps> => {
  const regionResortTopRatingOverall = await fetchGuideHomeTopRatedResorts(ResortTopRated.overall, 0, 30);
  const regions = await fetchRegionsListGuide();
  const featured = await fetchGuideHomeFeatured();
  const backgroundImage = await fetchBackgroundImages();

  return {
    type: 'global',
    route: 'ski-resorts',
    guideHomeTopRatedFirstPage: regionResortTopRatingOverall,
    regions,
    featured,
    backgroundImage,
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
  };
};

// This function gets called at build time
export const getGuidePageStaticProps = wrapStaticProps(fetchProps);
export const getGuidePageServerSideProps = wrapServerSideProps(fetchProps);
